import type { AuditLogDetailsQueryParams, AuditLogQueryParams } from "@/components/AuditLog/AuditLogInterface";
import CompanySettingService from "@/services/api/companySetting";
import { handleResponse } from "@/services/axios";
import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit"

interface CompanySettingState {
    currencies: any[];
    timeZones: any[];
    appSetting: any;
    getPermissionMail: any[] | null;
    companySettingAuditLogs: any[];
    userSettingAuditSummary: any[];
    userSettingAuditLogs: any[];
    hasMoreAuditLogs: boolean;
    userSettingsMailRecipient: any[];
    loading: {
        getPermissionMailLoading: boolean;
        userSettingMailRecipient: boolean;
        companySettingAuditLogs: boolean;
        userSettingAuditSummary: boolean;
        userSettingAuditLogs: boolean;
        currencies: boolean;
        timeZones: boolean;
        appSetting: boolean;
        updateCompany: boolean;
    };
    error: string | null;
}

const initialState: CompanySettingState = {
    currencies: [],
    timeZones: [],
    appSetting: null,
    getPermissionMail: null,
    companySettingAuditLogs: [],
    hasMoreAuditLogs: true,
    userSettingsMailRecipient: [],
    userSettingAuditSummary: [],
    userSettingAuditLogs: [],
    loading: {
        getPermissionMailLoading: false,
        userSettingMailRecipient: false,
        companySettingAuditLogs: false,
        userSettingAuditSummary: false,
        userSettingAuditLogs: false,
        currencies: false,
        timeZones: false,
        appSetting: false,
        updateCompany: false,
    },
    error: null,
}

export const getCurrencies = createAsyncThunk(
    "companySetting/currencies",
    async (_, { rejectWithValue }) => {
        try {
            const res = await CompanySettingService.getCurrencies();
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch Currencies");
        }
    }
)

export const getTimeZones = createAsyncThunk(
    "companySetting/TimeZones",
    async (_, { rejectWithValue }) => {
        try {
            const res = await CompanySettingService.getTimeZones();
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch TimeZones");
        }
    }
)

export const getAppSetting = createAsyncThunk(
    "companySetting/AppSetting",
    async (_, { rejectWithValue }) => {
        try {
            const res = await CompanySettingService.getAppSetting();
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch App Setting");
        }
    }
)

export const updateCompanyLogo = createAsyncThunk(
    "companySetting/companyLogo",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CompanySettingService.updateCompanyLogo(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to Update Company Logo");
        }
    }
)

export const updateCompanySetting = createAsyncThunk(
    "companySetting/updateSetting",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CompanySettingService.updateCompanySetting(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to Update Company Setting");
        }
    }
)

export const deleteCompany = createAsyncThunk(
    "companySetting/deleteCompany",
    async (_, { rejectWithValue }) => {
        try {
            const res = await CompanySettingService.deleteCompany();
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to Delete Company");
        }
    }
)

export const getPermissionMailRecipients = createAsyncThunk(
    "companySetting/getPermissionMailRecipients",
    async (_, { rejectWithValue }) => {
        try {
            const response = await CompanySettingService.getPermissionMailRecipients();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed");
        }
    },
)

export const getUserSettingsMailRecipient = createAsyncThunk(
    "companySetting/getUserSettingsMailRecipient",
    async (_, { rejectWithValue }) => {
        try {
            const response = await CompanySettingService.getUserSettingsMailRecipient();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch user setting recipient");
        }
    },
)

export const getCompanySettingAuditLogs = createAsyncThunk(
    "companySetting/getCompanySettingAuditLogs",
    async (params: { from: number; to: number }, { rejectWithValue }) => {
        try {
            const response = await CompanySettingService.getCompanySettingAuditLogs(params);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch audit logs");
        }
    }
);

export const getUserSettingAuditSummary = createAsyncThunk(
    "companySetting/getUserSettingAuditSummary",
    async (params: AuditLogQueryParams, { rejectWithValue }) => {
        try {
            const response = await CompanySettingService.getUserSettingAuditSummary(params);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch audit summary");
        }
    }
);

export const getUserSettingAuditLogs = createAsyncThunk(
    "companySetting/getUserSettingAuditLogs",
    async (params: AuditLogDetailsQueryParams, { rejectWithValue }) => {
        try {
            const response = await CompanySettingService.getUserSettingAuditLogs(params);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch audit log details");
        }
    }
);

const CompanySettingSlice = createSlice({
    name: "companySetting",
    initialState,
    reducers: {
        clearCompanySettingAuditLogs: (state) => {
            state.companySettingAuditLogs = [];
            state.hasMoreAuditLogs = true;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCurrencies.pending, (state) => {
                state.loading.currencies = true;
                state.error = null;
            })
            .addCase(getCurrencies.fulfilled, (state, action) => {
                state.currencies = action.payload || [];
                state.loading.currencies = false;
            })
            .addCase(getCurrencies.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.currencies = false;
            })

            .addCase(getTimeZones.pending, (state) => {
                state.loading.timeZones = true;
                state.error = null;
            })
            .addCase(getTimeZones.fulfilled, (state, action) => {
                state.timeZones = action.payload || [];
                state.loading.timeZones = false;
            })
            .addCase(getTimeZones.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.timeZones = false;
            })

            .addCase(getAppSetting.pending, (state) => {
                state.loading.appSetting = true;
                state.error = null;
            })
            .addCase(getAppSetting.fulfilled, (state, action) => {
                state.appSetting = action.payload || null;
                state.loading.appSetting = false;
            })
            .addCase(getAppSetting.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.appSetting = false;
            })

            .addCase(getPermissionMailRecipients.pending, (state) => {
                state.loading.getPermissionMailLoading = true;
                state.error = null;
            })
            .addCase(getPermissionMailRecipients.fulfilled, (state, action) => {
                state.loading.getPermissionMailLoading = false;
                state.getPermissionMail = action.payload || null;
            })
            .addCase(getPermissionMailRecipients.rejected, (state, action) => {
                state.loading.getPermissionMailLoading = false;
                state.error = action.payload as string;
            })

            .addCase(getUserSettingsMailRecipient.pending, (state) => {
                state.loading.userSettingMailRecipient = true;
            })
            .addCase(getUserSettingsMailRecipient.fulfilled, (state, action) => {
                state.loading.userSettingMailRecipient = false;
                state.userSettingsMailRecipient = action.payload || null;
            })
            .addCase(getUserSettingsMailRecipient.rejected, (state, action) => {
                state.loading.userSettingMailRecipient = false;
                state.error = action.payload as string;
            })

            .addCase(getCompanySettingAuditLogs.pending, (state) => {
                state.loading.companySettingAuditLogs = true;
                state.error = null;
            })
            .addCase(getCompanySettingAuditLogs.fulfilled, (state, action) => {
                state.loading.companySettingAuditLogs = false;
                const newLogs = Array.isArray(action.payload) ? action.payload : (action.payload?.data || action.payload?.Items || []);
                const isFirstPage = action.meta.arg?.from === 1;

                if (isFirstPage) {
                    state.companySettingAuditLogs = newLogs;
                } else {
                    state.companySettingAuditLogs = [...(state.companySettingAuditLogs || []), ...newLogs];
                }

                const totalCount = action.payload?.TotalRecords;

                if (newLogs.length === 0) {
                    state.hasMoreAuditLogs = false;
                } else if (totalCount !== undefined && totalCount !== null) {
                    state.hasMoreAuditLogs = state.companySettingAuditLogs.length < totalCount;
                } else if (newLogs.length < 50) {
                    state.hasMoreAuditLogs = false;
                } else {
                    state.hasMoreAuditLogs = true;
                }
            })
            .addCase(getCompanySettingAuditLogs.rejected, (state, action) => {
                state.loading.companySettingAuditLogs = false;
                state.hasMoreAuditLogs = false;
                state.error = action.payload as string;
            })

            .addCase(getUserSettingAuditSummary.pending, (state) => {
                state.loading.userSettingAuditSummary = true;
                state.error = null;
            })
            .addCase(getUserSettingAuditSummary.fulfilled, (state, action) => {
                state.loading.userSettingAuditSummary = false;
                state.userSettingAuditSummary = action.payload || [];
            })
            .addCase(getUserSettingAuditSummary.rejected, (state, action) => {
                state.loading.companySettingAuditLogs = false;
                state.error = action.payload as string;
            })

            .addCase(getUserSettingAuditLogs.pending, (state) => {
                state.loading.userSettingAuditLogs = true;
                state.error = null;
            })
            .addCase(getUserSettingAuditLogs.fulfilled, (state, action) => {
                state.loading.userSettingAuditLogs = false;
                state.userSettingAuditLogs = action.payload || [];
            })
            .addCase(getUserSettingAuditLogs.rejected, (state, action) => {
                state.loading.userSettingAuditLogs = false;
                state.error = action.payload as string;
            });
    }
})

export const {
    clearCompanySettingAuditLogs,
} = CompanySettingSlice.actions;

export default CompanySettingSlice.reducer;